import * as React from 'react';
import Descriptions from 'antd/lib/descriptions';
import Space from 'antd/lib/space';
import Tag from 'antd/lib/tag';
import Tooltip from 'antd/lib/tooltip';
import Text from 'antd/lib/typography/Text';
import { WarningFilled } from '@ant-design/icons';
import { IosDependenciesDependencyReport } from '@/types/iosDependencies';
import { rwColors } from '@/components/styles/globalStyles';
import {
    directnessLabel,
    formatConstraint,
    formatMinIOS,
    formatStatusLabel,
    statusTagColor,
    truncatedRevision,
} from '@/components/dependencies/iosDependenciesUtils';

const renderMinIOSCell = (value: string | undefined, incompatible: boolean, appMinIOSVersion?: string) => {
    if (!value) return '—';
    if (!incompatible) return formatMinIOS(value);
    const tooltip =
        appMinIOSVersion ?
            `Requires iOS ${value} but the app targets iOS ${appMinIOSVersion}`
        :   `Requires iOS ${value}, which is higher than the app's minimum supported iOS version`;
    return (
        <Space size={4}>
            <Text style={{ color: rwColors.statusYellow }}>{formatMinIOS(value)}</Text>
            <Tooltip title={tooltip}>
                <WarningFilled style={{ color: rwColors.statusYellow }} />
            </Tooltip>
        </Space>
    );
};

const DependencyDetailContent: React.FC<{
    row: IosDependenciesDependencyReport;
    appMinIOSVersion?: string;
}> = ({ row, appMinIOSVersion }) => {
    const directness = directnessLabel(row.dependencyGraph);
    const compatibilityText =
        row.latestCompatible === undefined ? null
        : row.latestCompatible ? 'Compatible with app min iOS'
        : 'Latest is not compatible with app min iOS';
    const latestExceeds = row.latestMinIOSExceedsAppMin === true;

    return (
        <Space direction="vertical" size={8} style={{ width: 360, maxWidth: '90vw' }}>
            <Space direction="vertical" size={0}>
                <Text strong>{row.identity}</Text>
                <Text type="secondary" style={{ fontSize: 12 }} copyable={{ text: row.location }}>
                    {row.location}
                </Text>
            </Space>
            <Descriptions size="small" column={1} colon={false} labelStyle={{ width: 130 }}>
                <Descriptions.Item label="Status">
                    <Tag color={statusTagColor(row.status)}>{formatStatusLabel(row.status)}</Tag>
                </Descriptions.Item>
                {compatibilityText && <Descriptions.Item label="Compatibility">{compatibilityText}</Descriptions.Item>}
                {row.checkError && (
                    <Descriptions.Item label="Check error">
                        <Text type="danger" style={{ fontSize: 12 }}>
                            {row.checkError}
                        </Text>
                    </Descriptions.Item>
                )}
                <Descriptions.Item label="Kind">{row.kind || '—'}</Descriptions.Item>
                <Descriptions.Item label="Source">
                    {directness.label === 'Direct' ?
                        <Tag color="magenta">Direct</Tag>
                    :   <Tag>{directness.label}</Tag>}
                </Descriptions.Item>
                {directness.chain && (
                    <Descriptions.Item label="Dependency tree">
                        <Text style={{ fontSize: 12 }}>{directness.chain}</Text>
                    </Descriptions.Item>
                )}
                <Descriptions.Item label="Current">{row.currentVersion || '—'}</Descriptions.Item>
                {row.revision && (
                    <Descriptions.Item label="Revision">
                        <Text code copyable={{ text: row.revision }}>
                            {truncatedRevision(row.revision)}
                        </Text>
                    </Descriptions.Item>
                )}
                <Descriptions.Item label="Dependency rule">
                    {row.versionConstraint ?
                        <Text code>{formatConstraint(row.versionConstraint)}</Text>
                    :   '—'}
                </Descriptions.Item>
                <Descriptions.Item label="Latest">{row.latestVersion || '—'}</Descriptions.Item>
                {row.latestPreReleaseVersion && (
                    <Descriptions.Item label="Latest pre-release">{row.latestPreReleaseVersion}</Descriptions.Item>
                )}
                <Descriptions.Item label="Latest same-major">{row.latestSameMajor || '—'}</Descriptions.Item>
                <Descriptions.Item label="Latest same-patch">{row.latestSamePatch || '—'}</Descriptions.Item>
                <Descriptions.Item label="Latest min support">
                    {renderMinIOSCell(row.latestMinIOS, latestExceeds, appMinIOSVersion)}
                </Descriptions.Item>
                <Descriptions.Item label="Latest compatible">{row.latestCompatibleVersion || '—'}</Descriptions.Item>
                <Descriptions.Item label="Latest compat. min support">
                    {renderMinIOSCell(row.latestCompatibleMinIOS, false, appMinIOSVersion)}
                </Descriptions.Item>
                {row.updatedTo && (
                    <Descriptions.Item label="If root upgraded">
                        <Space direction="vertical" size={0}>
                            <Text style={{ fontSize: 12 }}>
                                {row.updatedTo.root}@{row.updatedTo.rootVersion} → {row.updatedTo.newVersion}
                            </Text>
                            {row.updatedTo.possibleMatches && row.updatedTo.possibleMatches.length > 0 && (
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    Possible matches:{' '}
                                    {row.updatedTo.possibleMatches
                                        .map((m) => (m.version ? `${m.identity}@${m.version}` : m.identity))
                                        .join(', ')}
                                </Text>
                            )}
                        </Space>
                    </Descriptions.Item>
                )}
            </Descriptions>
        </Space>
    );
};

export default DependencyDetailContent;
